import { Link } from 'react-router-dom'
import type { Connector, ConnectorStatus } from '@app/shared'

import { useConnectors } from '../lib/data'
import { signOut, useSession } from '../lib/auth/useSession'
import { Section, PanelSkeleton, PanelUnavailable, StatusPill, ActionButton } from '../components/connections/parts'
import { ApiPath, FactCard, FactRow, NoteRow, SettingsScreen } from '../components/settings/parts'

// ============================================================================
// ACCOUNT — read-only identity, and nothing else.
//
// NO MOCKUP SOURCE (the handoff draws the nav row, not the screen). What decides
// its content is the first line of CLAUDE.md → Boundaries: "Single user, forever.
// No signup flow, no multi-tenant features." A display name, an avatar, a
// password form and an email-change flow are all things a multi-tenant app needs
// so that one user can be told apart from another. There is only one user. So
// this screen shows WHO the token says you are, WHAT is connected on your
// behalf, and the one action that changes any of it — signing out.
//
// The email and the user id come from the browser's own Supabase session, not
// from a fetch. `GET /api/whoami` returns the same uuid the hard way (through the
// Worker, from the verified JWT), and it is named below so the machine-readable
// half of this screen is discoverable rather than implied.
// ============================================================================

/** Same mapping the Connections screen's connector card uses. `planned` has no state to show. */
const PILL: Record<ConnectorStatus, { tone: 'success' | 'danger' | 'warning' | 'neutral'; label: string } | null> = {
  ok: { tone: 'success', label: 'OK' },
  error: { tone: 'danger', label: 'ERROR' },
  rate_limited: { tone: 'warning', label: 'RATE LIMITED' },
  planned: null,
  disconnected: { tone: 'neutral', label: 'NOT CONNECTED' },
}

export function Account() {
  const session = useSession()
  const connectors = useConnectors()

  return (
    <SettingsScreen active="account" title="Account" subtitle={subtitleFor(session.status)}>
      <Section label="Signed in as">
        {session.status === 'loading' && <PanelSkeleton height={92} />}

        {session.status === 'signed-in' && (
          <>
            <FactCard>
              <FactRow
                label="Email"
                value={session.session.user.email ?? null}
                missingReason="This session carries no email address. Nothing is shown rather than a placeholder."
              />
              <FactRow label="User id" value={session.session.user.id} />
            </FactCard>
            <div className="flex items-center gap-3">
              <ActionButton label="Sign out" variant="outline" size="md" onClick={() => void signOut()} />
              <p className="text-[11px] font-normal leading-[1.5] text-microcopy text-pretty">
                Ends the session in this browser. Nothing you have written is touched, and the
                theme and size you picked on Appearance stay put — they never reach the server.
              </p>
            </div>
          </>
        )}

        {session.status === 'demo' && (
          <NoteRow>
            Nobody is signed in, because this build has no account configured at all — it runs on
            the fixed demo dataset. There is no email and no user id to show, and inventing one
            would make a sample dataset look like your life.
          </NoteRow>
        )}

        {session.status === 'unreachable' && (
          <NoteRow tone="warning">
            {session.reason} The session could not be confirmed, so no email or user id is shown.
            Reload to check again.
          </NoteRow>
        )}

        {session.status === 'signed-out' && (
          <NoteRow tone="warning">
            The session ended while this page was open. Reload and sign in again; nothing has been
            lost.
          </NoteRow>
        )}
      </Section>

      <Section label="Connected sources">
        {connectors.isPending && <PanelSkeleton height={140} />}
        {connectors.error && <PanelUnavailable error={connectors.error} />}
        {connectors.data && (
          <>
            <FactCard>
              {connectors.data.map((connector) => (
                <SourceRow key={connector.id} connector={connector} />
              ))}
            </FactCard>
            <p className="text-[11.5px] font-normal leading-[1.5] text-ink-3 text-pretty">
              This is the roll-call, not the detail.{' '}
              <Link
                to="/settings/connections"
                className="underline underline-offset-2 hover:text-ink"
              >
                Connections
              </Link>{' '}
              is where each source says when it last synced, what it made stale, and what to do
              about it.
            </p>
          </>
        )}
      </Section>

      <Section label="Profile">
        <NoteRow>
          There is nothing here to edit. LifeOS has exactly one user and no way to add a second, so
          a display name would be a field only you would ever read and an avatar would be shown to
          nobody. Changing the email or the password happens in Supabase, which is where the
          account actually lives — this app never handles either.
        </NoteRow>
      </Section>

      <Section label="The same facts, for a machine">
        <NoteRow>
          <ApiPath>GET /api/whoami</ApiPath> returns the owner uuid derived from the verified
          token — the same id printed above, reached the honest way.{' '}
          <ApiPath>GET /api/connections</ApiPath> returns every source in this list, planned ones
          included. Privacy &amp; data links the full API index.
        </NoteRow>
      </Section>
    </SettingsScreen>
  )
}

function subtitleFor(status: ReturnType<typeof useSession>['status']): string | null {
  if (status === 'signed-in') return 'One owner, one workspace — nothing here is shared'
  if (status === 'demo') return 'Demo build · no account is configured'
  if (status === 'signed-out') return 'The session has ended'
  if (status === 'unreachable') return 'The session could not be confirmed'
  return null
}

/**
 * One source, at roll-call depth: what it is, what it reaches, and its state.
 * A `planned` connector gets the word "planned" and the version, never a pill —
 * there is no state to indicate for something that has never run.
 */
function SourceRow({ connector }: { connector: Connector }) {
  const pill = PILL[connector.status]
  return (
    <div className="flex items-center gap-[14px] px-[15px] py-[12px]">
      <div className="flex min-w-0 flex-1 flex-col gap-[4px]">
        <span className="text-[12px] font-semibold leading-none text-ink">{connector.name}</span>
        <span className="text-[10.5px] font-normal leading-[1.4] text-microcopy text-pretty">
          {connector.scope}
        </span>
      </div>
      {pill ? (
        <StatusPill tone={pill.tone} label={pill.label} />
      ) : (
        <span className="flex-none text-[10px] font-semibold uppercase leading-[1.7] tracking-[0.06em] text-faint">
          {connector.plannedFor ? `planned · ${connector.plannedFor}` : 'planned'}
        </span>
      )}
    </div>
  )
}
